import type { MaybeRef } from '@vueuse/core';
import { get } from '@vueuse/core';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import orderBy from 'lodash/orderBy';
import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed, ref } from 'vue';
import type { IBetBuilderMarket, IBetBuilderMatch, IBetBuilderSelection } from '@/interface/betBuilder';
import {
    BetBuilderDisplayType,
    BetBuilderSelectionStatus,
    MarketPage,
} from '@/interface/enum';
import { deepCompareComputed } from '@/libraries/composables/deepCompareComputed';
import { orderMarkets } from '@/libraries/oddsApi/betBuilderMarketRule';
import { useRootStore } from '@/stores/rootStore';
import type { BetBuilderEventFragment, BetBuilderMarketFragment, BetBuilderSelectionFragment, Maybe } from '@/types';
import { useBetBuilderEventQuery, useBetBuilderEventSubscription } from '@/types';

export function useBetBuilderMatch(matchId: MaybeRef<number>, isEnabled: Ref<boolean> = ref(true)) {
    const betBuilderMatch = ref<IBetBuilderMatch | null>();
    const { isWsConnected, marketPage } = storeToRefs(useRootStore());

    const variables = computed(() => ({
        query: {
            id: get(matchId),
        },
    }));

    const { onResult, loaded, loading, refetch } = useBetBuilderEventQuery(
        variables,
        deepCompareComputed(() => ({
            enabled: isEnabled.value,
            pollInterval: getPollingInterval(),
        })),
    );

    const { onSubscription } = useBetBuilderEventSubscription(
        variables,
        deepCompareComputed(() => ({
            enabled: isEnabled.value && isWsConnected.value,
        })),
    );

    function getPollingInterval() {
        if (isWsConnected.value) {
            return undefined;
        }

        return marketPage.value === MarketPage.Live ? 30000 : 60000;
    }

    const onUpdate = (result?: Maybe<BetBuilderEventFragment>) => {
        if (!result) {
            betBuilderMatch.value = null;
            return;
        }

        const newEvent = cloneDeep(formatBetBuilderMatch(result));
        if (isEqual(betBuilderMatch.value, newEvent)) return;
        betBuilderMatch.value = newEvent;
        window.eventEmitter.emit('betBuilderMatchUpdated', get(matchId));
    };

    onResult(onUpdate);
    onSubscription(onUpdate);

    return {
        betBuilderMatch,
        refetch,
        loaded,
        loading,
    };
}

function formatBetBuilderSelections(selections: BetBuilderSelectionFragment[]) {
    return selections.map((selection): IBetBuilderSelection => ({
        id: selection.id,
        type: {
            id: selection.selectionType.id,
            name: selection.selectionType.name,
        },
        status: BetBuilderSelectionStatus.fromOddsApi(selection.status),
    }));
}

export function formatMarkets(markets: BetBuilderMarketFragment[]) {
    return markets.map((market): IBetBuilderMarket => ({
        matchMarketId: market.matchMarketId,
        marketType: {
            id: market.marketType.id,
            name: market.marketType.name,
        },
        matchStatType: {
            id: market.matchStatType.id,
            name: market.matchStatType.name ?? '',
        },
        displayType: BetBuilderDisplayType.fromOddsApi(market.displayType),
        point: market.point,
        displayPriority: market.displayPriority ?? 0,
        selections: orderBy(formatBetBuilderSelections(market.selections), [selection => selection.type.id, selection => selection.id]),
    }));
}

function formatBetBuilderMatch(result: BetBuilderEventFragment): IBetBuilderMatch {
    const formattedMarkets = orderMarkets(formatMarkets(result.markets));

    return {
        matchId: result.matchId,
        markets: formattedMarkets,
    };
}
